import React, { useCallback, useEffect, useRef, useState } from 'react';
import CommentInput from './CommentInput';
import CommentList from './CommentList';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import EventCommentMapper from '../../../../mappers/EventCommentMapper';
import { useMutation } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import CREATE_EVENT_COMMENT from '../../../../queries/EventComments/createEventCommentMutation';
import EDIT_EVENT_COMMENT from '../../../../queries/EventComments/editEventCommentMutation';
import DELETE_EVENT_COMMENT from '../../../../queries/EventComments/deleteEventCommentMutation';
import { v4 as uuid } from 'uuid';
import { showToast } from '@shared/services/toastService';
import useClickOutsideHandler from '../../../../../layout/navigation-menu/utils/useClickOutsideHandler';
import { TOAST_MESSAGE_TYPES } from '@shared/constants/toastMessageTypes';
import _ from 'lodash';
import { useToastNotificationStore } from '../../../../../notifications/toastNotification/ToastNotificationStore';
import { TOAST_NOTIFICATION_STORE_FIELDS } from '../../../../../notifications/toastNotification/constants/toastNotificationStoreFields';
import { useShallow } from 'zustand/shallow';

const SAVE_EVENT_SUCCESS_MESSAGE = 'Comment has been saved successfully.';
const EDIT_EVENT_SUCCESS_MESSAGE = 'Comment has been edited successfully.';
const DELETE_EVENT_SUCCESS_MESSAGE = 'Comment has been deleted successfully.';

const EVENT_COMMENT_OPTION_TYPE = {
  GROUP: 'group',
  INDIVIDUAL: 'individual'
};

/**
 * @typedef {object} CommentSectionProps
 * @property {number} [eventId]
 * @property {Array<*>} [eventComments]
 * @property {Array<*>} [accessibilityOptions]
 * @property {boolean} [loading]
 */

/**
 * @param {CommentSectionProps} props
 */
const CommentSection = props => {
  const { eventId = 0, eventComments = [], accessibilityOptions = [], loading = false } = props;

  const setToastNotificationStore = useToastNotificationStore.getState().setToastNotificationStore;
  const focusedCommentId = useToastNotificationStore(
    useShallow(state => state.focusedComment?.commentId)
  );

  const [toggleStatus, setToggleStatus] = useState(true);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const startComponentRef = useRef(null);
  const focusedCommentRef = useRef(null);

  const options = _.sortBy(accessibilityOptions, [
    option => option.Accessibility,
    option => option.Title.toLowerCase()
  ]).map(getMappedOption);

  const comments = EventCommentMapper.toCommentList(eventComments);
  const selectedComment = selectedCommentId && getSelectedComment(selectedCommentId, comments);

  const [createEventComment, { error: createEventCommentError }] = useMutation(
    CREATE_EVENT_COMMENT,
    {
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'AppraiserEvent', Id: eventId }),
          fields: {
            AppraiserEventComments: (existingCommentRefs, { toReference }) => {
              const newCommentRef = toReference(data?.createEventComment, true);
              return existingCommentRefs
                ? [newCommentRef, ...existingCommentRefs]
                : [newCommentRef];
            }
          }
        });
      }
    }
  );

  const [editEventComment, { error: editEventCommentError }] = useMutation(EDIT_EVENT_COMMENT);

  const [deleteEventComment, { error: deleteEventCommentError }] = useMutation(
    DELETE_EVENT_COMMENT,
    {
      update: (cache, { data }) => {
        const normalizedId = cache.identify({
          Id: data.deleteEventComment,
          __typename: 'AppraiserEventComment'
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      }
    }
  );

  useEffect(() => {
    if (!focusedCommentRef?.current) {
      return;
    }

    //This may be improved when https://github.com/facebook/react/issues/23396 is fixed.
    const renderTimeout = setTimeout(() => {
      focusedCommentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);

    return () => {
      clearTimeout(renderTimeout);
    };
  }, [eventComments, focusedCommentId, loading]);

  const handleOutsideClick = useCallback(() => {
    setToastNotificationStore(TOAST_NOTIFICATION_STORE_FIELDS.EVENT_COMMENT, null);
  }, [setToastNotificationStore]);

  useClickOutsideHandler(focusedCommentRef, handleOutsideClick);

  const changeToggleStatus = () => {
    setToggleStatus(prevState => !prevState);
  };

  const saveComment = async comment => {
    const selectedOption = options.find(option => option.value === comment.CommentTarget);
    const commentInput = {
      Id: comment.Id,
      Comment: comment.Comment,
      TargetUserId: selectedOption.targetUserId,
      Accessibility: selectedOption.accessibility
    };

    if (commentInput.Id) {
      return await saveEditedComment(commentInput);
    }

    await saveNewComment(commentInput);
  };

  const saveEditedComment = async comment => {
    resetSelectedCommentId();

    await editEventComment({
      variables: {
        comment
      }
    });

    showToast(TOAST_MESSAGE_TYPES.SUCCESS, EDIT_EVENT_SUCCESS_MESSAGE);
  };

  const saveNewComment = async comment => {
    await createEventComment({
      variables: {
        comment: { ...comment, EventId: eventId }
      }
    });

    showToast(TOAST_MESSAGE_TYPES.SUCCESS, SAVE_EVENT_SUCCESS_MESSAGE);
  };

  const editComment = commentId => {
    setSelectedCommentId(commentId);
    startComponentRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const deleteComment = async commentId => {
    await deleteEventComment({ variables: { id: commentId } });
    showToast(TOAST_MESSAGE_TYPES.SUCCESS, DELETE_EVENT_SUCCESS_MESSAGE);
  };

  const resetSelectedCommentId = () => setSelectedCommentId(null);

  if (createEventCommentError || editEventCommentError || deleteEventCommentError) {
    return <ApolloError />;
  }

  return (
    <Accordion
      ref={startComponentRef}
      expanded={toggleStatus}
      slotProps={{
        transition: { unmountOnExit: true }
      }}>
      <AccordionSummary
        onClick={changeToggleStatus}
        expandIcon={<ExpandMoreIcon data-testid='comment-expand-icon' />}>
        <h3>Comments Section</h3>
      </AccordionSummary>
      <AccordionDetails>
        <SkeletonWrapper show={loading} inheritChildHeight>
          <div className='comment-section' data-testid='comment-section'>
            <CommentInput
              key={uuid()}
              comment={selectedComment}
              accessibilityOptions={options}
              saveComment={saveComment}
              onCancel={resetSelectedCommentId}
            />
            <CommentList
              comments={comments}
              selectedCommentId={selectedCommentId}
              focusedCommentId={focusedCommentId}
              focusedCommentRef={focusedCommentRef}
              editComment={editComment}
              deleteComment={deleteComment}
            />
          </div>
        </SkeletonWrapper>
      </AccordionDetails>
    </Accordion>
  );
};

const getMappedOption = option => ({
  targetUserId: option.TargetUserId,
  accessibility: option.Accessibility,
  title: getOptionTitle(option),
  value: getCommentTarget(option.TargetUserId, option.Accessibility)
});

const getSelectedComment = (selectedCommentId, eventComments) => {
  const selectedComment = eventComments.find(comment => comment.id === selectedCommentId);
  return {
    Id: selectedComment.id,
    Comment: selectedComment.comment,
    CommentTarget: arrayToString(
      selectedComment.accessibilities.map(accessibility =>
        getCommentTarget(accessibility.targetUserId, accessibility.accessibility)
      )
    )
  };
};

const getOptionTitle = option => {
  const prefix = option.TargetUserId ? 'ATM - ' : '';
  return `${prefix}${option.Title}`;
};

const getCommentTarget = (targetUserId, accessibility) =>
  targetUserId
    ? `${EVENT_COMMENT_OPTION_TYPE.INDIVIDUAL}${targetUserId}`
    : `${EVENT_COMMENT_OPTION_TYPE.GROUP}${accessibility}`;

const arrayToString = array => array.join('');

export default CommentSection;
